/* eslint-disable react/jsx-props-no-spreading */
import { Pagination, Spinner, Button } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import { Text as TextField } from '@micromag/fields';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useHistory, useSearch } from 'wouter';

import useStories from '../../hooks/useStories';

import ArrowButton from '../buttons/Arrow';
import CloseButton from '../buttons/Close';
import StoriesList from '../lists/Stories';
import EmptyResults from './EmptyResults';
import EmptyStories from './EmptyStories';

import styles from '../../../styles/partials/all-stories.module.scss';

const propTypes = {
    route: PropTypes.string,
    itemLink: PropTypes.string,
    count: PropTypes.number,
    asList: PropTypes.bool,
    withSearch: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    route: 'home',
    itemLink: 'stories.show',
    count: 8,
    asList: false,
    withSearch: false,
    className: null,
};

const AllStories = ({ route, itemLink, count, asList, withSearch, className }) => {
    const intl = useIntl();
    const url = useUrlGenerator();
    const [pathname, setLocation] = useLocation();
    const searchString = useSearch();

    const {
        page = 1,
        last = null,
        search = null,
    } = useMemo(
        () =>
            searchString !== null && searchString.length > 0 ? queryString.parse(searchString) : {},
        [searchString],
    );

    const finalQuery = useMemo(
        () => ({ last: last != null ? last : null, search, preview: true }),
        [page, last, search],
    );

    const paginationUrl = useMemo(() => url(route), [url, route]);

    const { stories, total, lastPage, loading } = useStories(finalQuery, page, count);

    const [searchValue, setSearchValue] = useState(search);
    const submitSearch = useCallback(
        (e) => {
            e.preventDefault();
            setLocation(
                `${pathname}${
                    searchValue !== null ? `?${queryString.stringify({ search: searchValue })}` : ''
                }`,
            );
        },
        [setLocation, pathname, searchValue],
    );

    const clearSearch = useCallback(() => {
        setSearchValue(null);
        setLocation(pathname);
    }, [setLocation, pathname, setSearchValue]);

    const searchInput = withSearch ? (
        <form className={classNames([styles.searchWrapper, 'col px-1'])} onSubmit={submitSearch}>
            <TextField
                value={searchValue}
                className={styles.search}
                placeholder={intl.formatMessage({
                    defaultMessage: 'Search',
                    description: 'Input placeholder',
                })}
                onChange={(val) => setSearchValue(val)}
            />
            {search !== null ? (
                <CloseButton className={styles.close} onClick={clearSearch} />
            ) : (
                <ArrowButton className={styles.close} onClick={submitSearch} />
            )}
        </form>
    ) : null;

    const loader = loading ? (
        <Spinner className={styles.loading} />
    ) : (
        <div className={styles.loading} />
    );

    const [finalStories, setFinalStories] = useState(stories);
    useEffect(() => {
        if (loading === false) {
            setFinalStories(stories);
        }
    }, [loading]);

    const empty = !loading && total === 0;
    const emptyElement =
        search !== null ? (
            <EmptyResults
                className={styles.emptyStories}
                button={
                    <Button href={pathname} theme="primary" size="lg">
                        <FormattedMessage
                            defaultMessage="Clear search"
                            description="Button label"
                        />
                    </Button>
                }
            />
        ) : (
            <EmptyStories className={styles.emptyStories} />
        );

    return (
        <section className={classNames([styles.container, { [className]: className !== null }])}>
            <div className="d-flex flex-row align-items-flex-start justify-content-between mw-100 pt-2 pe-2 overflow-scroll">
                <div className="d-flex align-items-flex-start flex-wrap">
                    {searchInput}
                    {loader}
                </div>
                <div>
                    {lastPage > 1 ? (
                        <Pagination
                            page={parseInt(page, 10)}
                            query={finalQuery}
                            lastPage={lastPage}
                            total={total}
                            url={paginationUrl}
                        />
                    ) : null}
                </div>
            </div>
            <div className={classNames([styles.inner, { [styles.disabled]: loading }])}>
                <StoriesList
                    items={finalStories}
                    itemLink={itemLink}
                    itemType={asList ? 'list' : 'card'}
                    className={classNames([styles.list, { [styles.empty]: empty }])}
                />
                {empty && !asList ? emptyElement : null}
            </div>
            <div className="d-flex flex-row align-items-center justify-content-end mw-100 pt-2 pe-2 overflow-scroll">
                {lastPage > 1 ? (
                    <Pagination
                        page={parseInt(page, 10)}
                        query={finalQuery}
                        lastPage={lastPage}
                        total={total}
                        url={paginationUrl}
                    />
                ) : null}
            </div>
        </section>
    );
};

AllStories.propTypes = propTypes;
AllStories.defaultProps = defaultProps;

export default AllStories;
